import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from '@frontend/features/common/components/Link';
import { Table } from '@frontend/features/common/components/Table/Table';
import { routes } from '@frontend/router/routes';
import { MetricLabel } from './PerformanceHelp';
import { ConversionReport, PerformanceRequest } from './types';

export const conversionPercent = (rate: number | null) =>
  rate === null ? '—' : `${(rate * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

export function ConversionMetrics({
  report,
  request,
  onRequest,
}: {
  report: ConversionReport;
  request: PerformanceRequest;
  onRequest: (request: PerformanceRequest) => void;
}) {
  const { t } = useTranslation();
  const text = (key: string) => t(`workflowPerformance.${key}`);
  const filter = (values: Partial<PerformanceRequest>) =>
    onRequest({ ...request, afterConversionCaseId: undefined, afterConversionAdvisor: undefined, ...values });
  const advisors = new Map(
    report.filters.filter((row) => row.advisor).map((row) => [row.advisor!, row.advisorName ?? row.advisor!]),
  );
  const teams = new Map(
    report.filters.filter((row) => row.team).map((row) => [row.team!, row.teamName ?? String(row.team!)]),
  );
  const origins = [...new Set(report.filters.flatMap((row) => (row.origin ? [row.origin] : [])))];
  const keys = [
    'received',
    'eligible',
    'won',
    'rate',
    'lost',
    'closed',
    'open',
    'transferred',
    'otherSuccess',
    'alreadySuccessful',
    'unknown',
  ] as const;
  return (
    <Stack spacing={2}>
      <Typography variant="h6">{text('conversion')}</Typography>
      <Alert severity="info">
        {text(report.grain === 'advisor' ? 'conversionAdvisorHint' : 'conversionWorkflowHint')}
      </Alert>
      <Typography>{text('conversionRulesHint')}</Typography>
      <Typography>
        {t('workflowPerformance.conversionCutoffAt', { at: new Date(report.cutoff).toLocaleString() })}
      </Typography>
      {report.truncated && (
        <Alert severity="warning">{t('workflowPerformance.conversionLimit', { limit: report.limit })}</Alert>
      )}
      {report.groupsTruncated && <Alert severity="warning">{text('groupsTruncated')}</Alert>}
      {(request.advisor || request.team || request.origin) && (
        <Button onClick={() => filter({ advisor: undefined, team: undefined, origin: undefined })}>
          {text('all')}
        </Button>
      )}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {report.grain === 'advisor' && (
          <TextField
            select
            label={text('workloadAdvisor')}
            value={request.advisor ?? ''}
            onChange={(event) => filter({ advisor: event.target.value || undefined })}
          >
            <MenuItem value="">{text('all')}</MenuItem>
            {[...advisors].map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          label={text('workloadTeam')}
          value={request.team ?? ''}
          onChange={(event) => filter({ team: event.target.value ? Number(event.target.value) : undefined })}
        >
          <MenuItem value="">{text('all')}</MenuItem>
          {[...teams].map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label={text('origin')}
          value={request.origin ?? ''}
          onChange={(event) => filter({ origin: event.target.value || undefined })}
        >
          <MenuItem value="">{text('all')}</MenuItem>
          {origins.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`cases.originatedByChannel.${value}`, { defaultValue: value })}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {report.totals && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Typography>
              {t('workflowPerformance.conversionTotals', {
                ...report.totals,
                rate: conversionPercent(report.totals.rate),
              })}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
              {(['eligible', 'won', 'rate'] as const).map((key) => (
                <Stack key={key} spacing={1}>
                  <MetricLabel label={text(`conversion_${key}`)} help={text(`help.conversion_${key}`)} />
                  <Typography variant="h4">
                    {key === 'rate' ? conversionPercent(report.totals!.rate) : report.totals![key]}
                  </Typography>
                </Stack>
              ))}
            </Box>
          </Stack>
        </Paper>
      )}
      <Table
        height={340}
        columns={[
          { id: 'advisor', label: text('workloadAdvisor') },
          { id: 'team', label: text('workloadTeam') },
          { id: 'origin', label: text('origin') },
          ...keys.map((id) => ({
            id,
            label: <MetricLabel label={text(`conversion_${id}`)} help={text(`help.conversion_${id}`)} />,
          })),
        ]}
        data={{
          key: 'id',
          totalCount: report.groups.length,
          items: report.groups.map((row, id) => ({
            ...row,
            id,
            rate: conversionPercent(row.rate),
            advisor: row.advisor ? (
              <Button onClick={() => filter({ advisor: row.advisor! })}>{row.advisorName ?? row.advisor}</Button>
            ) : (
              text('conversionWorkflow')
            ),
            team: row.team ? (
              <Button onClick={() => filter({ team: row.team! })}>{row.teamName ?? row.team}</Button>
            ) : (
              text('unassigned')
            ),
            origin: row.origin ? (
              <Button onClick={() => filter({ origin: row.origin! })}>
                {t(`cases.originatedByChannel.${row.origin}`, { defaultValue: row.origin })}
              </Button>
            ) : (
              text('unassigned')
            ),
          })),
        }}
      />
      <Typography variant="h6">{text('conversionDetails')}</Typography>
      <Table
        height={320}
        columns={[
          { id: 'case', label: text('case') },
          { id: 'advisor', label: text('workloadAdvisor') },
          { id: 'received', label: text('conversionReceivedAt') },
          { id: 'result', label: text('responseResult') },
          { id: 'success', label: text('conversionSuccessAt') },
        ]}
        data={{
          key: 'id',
          totalCount: report.cases.length,
          items: report.cases.map((row) => ({
            ...row,
            id: `${row.caseId}:${row.advisor ?? ''}`,
            case: <Link to={routes.dashboard.caseView.replace(':id', String(row.caseId))}>#{row.caseId}</Link>,
            advisor: row.advisorName ?? row.advisor ?? text('conversionWorkflow'),
            received: new Date(row.receivedAt).toLocaleString(),
            result: text(`conversion_${row.result}`),
            success: row.successAt && row.result !== 'unknown' ? new Date(row.successAt).toLocaleString() : '—',
          })),
        }}
      />
      <Stack direction="row">
        <Button disabled={!request.afterConversionCaseId} onClick={() => filter({})}>
          {text('previous')}
        </Button>
        <Button
          disabled={!report.next}
          onClick={() =>
            report.next &&
            onRequest({
              ...request,
              afterConversionCaseId: report.next.caseId,
              afterConversionAdvisor: report.next.advisor ?? undefined,
            })
          }
        >
          {text('next')}
        </Button>
      </Stack>
    </Stack>
  );
}
